import StoryblokClient from "storyblok-js-client";
import { renderRichText } from '@storyblok/richtext';
import { makeAutoObservable } from "mobx";
import { get } from 'lodash';

// Component identifiers to fetch content by storyblok slugs.
const ENTRIES = {
  footer: 'footer',
  home: 'homepage',
  homeInsurance: 'home-insurance',
  homeQuickQuote: 'home-insurance/quick-quote',
  lifeInsurance: 'life-insurance',
  lifeInsuranceCoverages: 'life-insurance/types-of-coverages',
  motorQuickQuote: 'motor-insurance/quick-quote',
  privacyPolicy: 'legal/privacy-policy',
  termsOfUse: 'legal/terms-of-use',
  groupHealth: 'business-insurance/group-health',
  groupLife: 'business-insurance/group-life',
  smallBusiness: 'business-insurance/small-business',
  aboutUs: 'about-us',
  claimsSupport: 'customer-care/claims-support',
  contactUs: 'customer-care/contact-us',
  paymentOptions: 'customer-care/payment-options',
  renewals: 'customer-care/renewals',
  insurerContacts: 'customer-care/insurer-contacts',
  faq: 'customer-care/faq',
};

class ContentStore {
  storyBlockClient = new StoryblokClient({
    accessToken: process.env.REACT_APP_STORYBLOK_TOKEN,
  });

  IS_LOADING = -1;
  IS_FETCH_ERROR = -2;
  IS_NOT_LOADED = -3;

  // Fetched page content store, keyed by Storyblok slug.
  content = Object.entries(ENTRIES).reduce((obj, curr) =>
    ({ ...obj, [curr[1]]: this.IS_NOT_LOADED, }), {});

  currentPage; // Define which page is currently being shown.

  constructor() {
    makeAutoObservable(this);
  }

  getBlocks(componentName) {
    if (this.content[componentName] === undefined) {
      return (key, defaultValue = "Nothing loaded.") => defaultValue;
    }
    return (key, defaultValue) => get(this.content[componentName], key, defaultValue);
  }

  fetchBlock = async blockName => {
    if (this.content[blockName] === this.IS_NOT_LOADED) {
      this.__fetchBlock(blockName);
    }
  }

  fetchGlobalBlocks = async () => {
    if (this.content.footer === this.IS_NOT_LOADED) {
      this.__fetchBlock('footer');
    }
  }

  __fetchBlock = async blockName => {
    this.content[blockName] = this.IS_LOADING;
    const version = process.env.REACT_APP_STORYBLOK_VERSION || "published";

    try {
      const res = await this.storyBlockClient.get(`cdn/stories/${blockName}`, { version });
      this.content[blockName] = res.data.story.content;
    } catch (err) {
      this.content[blockName] = this.IS_FETCH_ERROR;
    }
  };

  renderComponent(component) {
    if (!component) {
      return '';
    }

    if (this.storyBlockClient.richTextResolver && typeof this.storyBlockClient.richTextResolver.render === 'function') {
      return this.storyBlockClient.richTextResolver.render(component);
    }

    try {
      return renderRichText(component);
    } catch (err) {
      console.warn('Storyblok rich text rendering failed:', err, component);
      return '';
    }
  }
}

export { ENTRIES };

export default ContentStore
